import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

export default class extends Controller {
  static values = {
    url: String,
    resourceName: { type: String, default: 'component' }
  }

  connect() {
    // Only enable sortable if user is authenticated
    if (!document.body.classList.contains('authenticated')) {
      return
    }

    // A container's own child list lives inside a draggable item of its
    // ancestor list. When SortableJS repositions that item mid-drag,
    // Stimulus's MutationObserver can see the reparented subtree as
    // removed-then-readded and disconnect/reconnect this controller even
    // though the element never left the document (see disconnect() below).
    // If we already have a live instance from before that false-positive
    // disconnect, keep using it instead of creating a duplicate.
    if (this.sortable) {
      return
    }

    this.sortable = Sortable.create(this.element, {
      animation: 150,
      handle: '.drag-handle',
      ghostClass: 'sortable-ghost',
      // Container components nest a sortable list of their children inside
      // a draggable item of another sortable list (their siblings). Every
      // list shares this group but with pull/put both denied, so an item
      // can never leave its own list or be accepted by a different one.
      group: { name: 'page-components', pull: false, put: false },
      onEnd: this.onEnd.bind(this)
    })
  }

  onEnd(event) {
    // Get all resource IDs in their new order
    const resourceIdKey = `${this.resourceNameValue}Id`
    const resourceIds = Array.from(this.element.children).map(el => el.dataset[resourceIdKey])

    const paramKey = `${this.resourceNameValue}_ids`

    fetch(this.urlValue, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: JSON.stringify({
        [paramKey]: resourceIds
      })
    })
    .then(response => {
      if (!response.ok) {
        console.error(`HTTP error! status: ${response.status}`)
      }
      return response.text()
    })
    .then(html => {
      // Turbo will handle the stream response to add the component
      Turbo.renderStreamMessage(html)

      // Reset the select
      event.target.value = ''
    })
  }

  disconnect() {
    // SortableJS's own destroy() calls its internal _onDrop() with no
    // event, which unconditionally wipes its module-level (shared across
    // every Sortable instance on the page) drag-tracking state as a side
    // effect. If this element is still attached to the document, this
    // disconnect is the false positive described in connect() above -
    // tearing down here would corrupt any drag actually in progress
    // elsewhere on the page. Only destroy for a genuine removal.
    if (this.element.isConnected) {
      return
    }

    if (this.sortable) {
      this.sortable.destroy()
      this.sortable = null
    }
  }
}
